import React from 'react';
import { X, Printer, FileDown, CreditCard, Building2, DollarSign, Fuel, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import type { AdminTransaction } from '../../services/api';
import Button from '../ui/Button';
import { formatAmount, getRunningBalance, getTransactionCid, downloadReceiptFile, printReceipt } from '../../utils/receiptGenerator';
import './TransactionDetailsModal.css';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: AdminTransaction | null;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  if (!isOpen || !transaction) return null;

  const metadata = transaction.metadata || {};
  const statusStr = (transaction.status || 'completed').toLowerCase();
  const isCompleted = statusStr === 'completed';
  const isFailed = statusStr === 'failed';
  const isPending = statusStr === 'pending';

  const formattedDate = new Date(transaction.createdAt).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const formattedTime =
    transaction.transactionTime ||
    new Date(transaction.createdAt).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const cid = getTransactionCid(transaction);
  const runningBal = getRunningBalance(transaction);
  const companyName =
    transaction.user?.company?.legalName ||
    (transaction.user ? `${transaction.user.firstName} ${transaction.user.lastName}` : 'N/A');

  const station = transaction.merchantName || metadata.station || transaction.description || 'N/A';
  const vehiclePlate = transaction.vehicle?.plateNumber || metadata.vehiclePlate || 'N/A';
  const litres = metadata.litres ? `${metadata.litres} L` : null;
  const price = metadata.price ? formatAmount(metadata.price, transaction.currency) : null;
  const odometer = metadata.importedOdometer || metadata.odometer ? `${metadata.importedOdometer || metadata.odometer} km` : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="transaction-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="td-modal-header">
          <div className="td-header-left">
            <div className="td-icon-box">
              <CreditCard size={22} color="#ff6b35" />
            </div>
            <div>
              <h2 className="td-title">Transaction Details</h2>
              <p className="td-subtitle">
                Reference: <span className="td-ref-badge">{transaction.reference || transaction.id}</span>
              </p>
            </div>
          </div>
          <button className="td-close-btn" onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="td-modal-body custom-scroll">
          {/* Status & Amount Hero Banner */}
          <div className={`td-hero-banner ${statusStr}`}>
            <div className="td-hero-info">
              <div className="td-status-tag">
                {isCompleted && <CheckCircle2 size={16} />}
                {isFailed && <AlertCircle size={16} />}
                {isPending && <AlertTriangle size={16} />}
                <span>{transaction.status.toUpperCase()}</span>
              </div>
              <div className="td-type-tag">
                {(transaction.type || '').replace(/_/g, ' ')}
              </div>
            </div>
            <div className="td-hero-amounts">
              <div className="td-amount-col">
                <span className="td-amount-label">Transaction Amount</span>
                <span className="td-amount-val">{formatAmount(transaction.amount, transaction.currency)}</span>
              </div>
              <div className="td-balance-col">
                <span className="td-balance-label">Running Balance</span>
                <span className="td-balance-val">{runningBal}</span>
              </div>
            </div>
          </div>

          {/* Section: Customer & CID */}
          <div className="td-card">
            <div className="td-card-header">
              <Building2 size={18} className="td-card-icon" />
              <h3>Account & Customer Information</h3>
            </div>
            <div className="td-grid-2">
              <div className="td-info-item">
                <span className="td-label">Customer ID (CID)</span>
                <span className="td-value td-cid-highlight">{cid}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Company Name</span>
                <span className="td-value">{companyName}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">User / Cardholder</span>
                <span className="td-value">
                  {transaction.user ? `${transaction.user.firstName} ${transaction.user.lastName}` : 'N/A'}
                </span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Email Address</span>
                <span className="td-value">{transaction.user?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Section: Transaction Audit Data */}
          <div className="td-card">
            <div className="td-card-header">
              <DollarSign size={18} className="td-card-icon" />
              <h3>Financial & Audit Data</h3>
            </div>
            <div className="td-grid-3">
              <div className="td-info-item">
                <span className="td-label">Base Amount</span>
                <span className="td-value">{formatAmount(transaction.amount, transaction.currency)}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Processing Fee</span>
                <span className="td-value">{transaction.fee ? formatAmount(transaction.fee, transaction.currency) : '₦0.00'}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Total Amount</span>
                <span className="td-value" style={{ fontWeight: 700, color: '#111827' }}>
                  {transaction.totalAmount ? formatAmount(transaction.totalAmount, transaction.currency) : formatAmount(transaction.amount, transaction.currency)}
                </span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Date</span>
                <span className="td-value">{formattedDate}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Time</span>
                <span className="td-value">{formattedTime}</span>
              </div>
              <div className="td-info-item">
                <span className="td-label">Provider Trans ID</span>
                <span className="td-value">{transaction.onafriqTransactionId || '—'}</span>
              </div>
            </div>
            {transaction.description && (
              <div className="td-desc-box">
                <span className="td-label">Description / Remarks</span>
                <p className="td-desc-text">{transaction.description}</p>
              </div>
            )}
          </div>

          {/* Section: Fuel & Vehicle Details (if applicable) */}
          {(station !== 'N/A' || vehiclePlate !== 'N/A' || litres || price) && (
            <div className="td-card">
              <div className="td-card-header">
                <Fuel size={18} className="td-card-icon" />
                <h3>Fuel & Vehicle Breakdown</h3>
              </div>
              <div className="td-grid-3">
                <div className="td-info-item">
                  <span className="td-label">Vehicle Plate</span>
                  <span className="td-value" style={{ fontWeight: 700, color: '#111827' }}>{vehiclePlate}</span>
                </div>
                <div className="td-info-item">
                  <span className="td-label">Station / Merchant</span>
                  <span className="td-value">{station}</span>
                </div>
                <div className="td-info-item">
                  <span className="td-label">Litres Dispensed</span>
                  <span className="td-value" style={{ color: '#047857', fontWeight: 700 }}>{litres || '—'}</span>
                </div>
                {price && (
                  <div className="td-info-item">
                    <span className="td-label">Price / Litre</span>
                    <span className="td-value">{price}</span>
                  </div>
                )}
                {odometer && (
                  <div className="td-info-item">
                    <span className="td-label">Odometer Reading</span>
                    <span className="td-value">{odometer}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="td-modal-footer">
          <div className="td-footer-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => printReceipt(transaction)}
              className="td-print-btn"
            >
              <Printer size={16} />
              <span>Print Receipt</span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => downloadReceiptFile(transaction)}
              className="td-download-btn"
            >
              <FileDown size={16} />
              <span>Download Receipt</span>
            </Button>
          </div>
          <Button type="button" variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
